import { lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isStandalone } from "../lib/install";
import {
  abrirPuerta,
  activarCandado,
  biometriaDisponible,
  candadoActivo,
  candadoRechazado,
  nombreBiometria,
  olvidarLlave,
  rechazarCandado,
  yaDesbloqueado,
} from "../lib/biometricLock";

// La puerta es la pantalla de login de Android copiada 1:1. Diferida: sólo la
// baja quien tiene el candado encendido.
// Sin módulo = el chunk es de una versión vieja y main.tsx ya está recargando
// (vite:preloadError): se queda en la espera, igual que `diferida` en App.tsx.
const IngresoApp = lazy(() =>
  import("./IngresoApp").then((m) => (m ? { default: m.IngresoApp } : new Promise<never>(() => {}))),
);

/**
 * Puerta biométrica de la PWA instalada — gemelo de la puerta de `LoginScreen`
 * en Android.
 *
 *  - Con sesión guardada + candado encendido + arranque en frío: la pantalla de
 *    login de Android en modo puerta ("Te damos la bienvenida", la cuenta,
 *    Ingresar con Biometría, Loguearse con mail y contraseña). La biometría
 *    espera el toque del botón, como en Android.
 *  - Sin candado decidido, la primera vez que hay sesión y el dispositivo
 *    puede: una hoja ofrece encenderlo (crear la llave exige un toque, por eso
 *    no viene encendido como en Android). "Ahora no" lo deja apagado; se prende
 *    desde el Perfil.
 *  - Quien abrió la puerta con contraseña o código vuelve a ver la oferta: la
 *    llave de este teléfono puede haber desaparecido (y el navegador no lo
 *    dice), así que se la vuelve a crear en vez de pedirle la contraseña cada
 *    vez que abre.
 *  - Sólo en la PWA instalada: en una pestaña del navegador pedir huella al
 *    abrir cada vez sería un estorbo.
 */
export function BiometricLockGate({ children }: { children: ReactNode }) {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const autenticado = state.kind === "authenticated";
  const instalada = isStandalone();
  const bio = nombreBiometria();

  const [bloqueado, setBloqueado] = useState<boolean>(
    () => instalada && candadoActivo() && !yaDesbloqueado(),
  );
  // null = no se ofrece; "nueva" = primera vez; "otra_vez" = entró con clave.
  const [oferta, setOferta] = useState<"nueva" | "otra_vez" | null>(null);
  // Entró con contraseña desde la puerta: la oferta dice "de nuevo".
  const [reofrecer, setReofrecer] = useState(false);
  const [activando, setActivando] = useState(false);
  const [avisoOferta, setAvisoOferta] = useState<string | null>(null);

  // Adentro y sin puerta (entró recién con contraseña, o ya la abrió): queda
  // anotado para este arranque. Sin esto, quien inicia sesión con la clave y
  // recibe una actualización (que recarga la página) veía la puerta enseguida.
  useEffect(() => {
    if (autenticado && !bloqueado) abrirPuerta();
  }, [autenticado, bloqueado]);

  // Sin sesión no hay nada que proteger: el login normal manda.
  useEffect(() => {
    if (state.kind === "anonymous" && bloqueado) setBloqueado(false);
  }, [state.kind, bloqueado]);

  // Oferta de encendido (nunca con la puerta cerrada encima).
  useEffect(() => {
    if (!autenticado || !instalada || bloqueado || candadoActivo() || candadoRechazado()) return;
    let vigente = true;
    void biometriaDisponible().then((ok) => {
      if (vigente && ok) setOferta(reofrecer ? "otra_vez" : "nueva");
    });
    return () => {
      vigente = false;
    };
  }, [autenticado, instalada, bloqueado, reofrecer]);

  const entrar = useCallback((viaClave: boolean) => {
    if (viaClave) {
      abrirPuerta();
      olvidarLlave();
      setReofrecer(true);
    }
    setBloqueado(false);
  }, []);

  if (bloqueado && state.kind === "authenticated") {
    return (
      <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#060c1e" }} />}>
        <IngresoApp
          puerta={{ email: state.profile.email, onEntrar: entrar }}
          onCrearCuenta={() => {
            // Cuenta nueva = esta sesión se cierra; el registro toma el mando.
            // La puerta sigue puesta hasta que la sesión ya no está (el efecto
            // de "anonymous" la saca): destrabar antes mostraba la app un
            // instante.
            void logout().then(() => navigate("/registro"));
          }}
        />
      </Suspense>
    );
  }

  const activar = async () => {
    if (state.kind !== "authenticated") return;
    const p = state.profile;
    setActivando(true);
    setAvisoOferta(null);
    const ok = await activarCandado({ id: p.id, email: p.email, nombre: p.displayName || p.email });
    setActivando(false);
    if (ok) {
      setOferta(null);
    } else {
      // Canceló el diálogo del teléfono (o no lo dejó): la hoja se queda y la
      // decisión la toma él, no un toque de más en el diálogo del sistema.
      setAvisoOferta("No se activó. Podés probar de nuevo o dejarlo para después.");
    }
  };
  const ahoraNo = () => {
    rechazarCandado();
    setOferta(null);
    setAvisoOferta(null);
  };

  return (
    <>
      {children}
      {oferta &&
        state.kind === "authenticated" &&
        createPortal(
          <div className="ctx-backdrop" role="presentation">
            <div className="push-nudge" role="dialog" aria-modal="true" aria-label={`Entrar con ${bio}`}>
              <span className="push-nudge__icon" aria-hidden>🔒</span>
              <h2 className="push-nudge__title">
                {oferta === "otra_vez" ? `¿Volvemos a activar ${bio}?` : `¿Entrar con ${bio}?`}
              </h2>
              <p className="push-nudge__body">
                {oferta === "otra_vez"
                  ? `Esta vez entraste con tu contraseña. Si lo activás de nuevo, la próxima vez que abras FORJA entrás con ${bio}.`
                  : `La próxima vez que abras FORJA entrás con ${bio}, sin escribir la contraseña.`}{" "}
                Tu teléfono te va a preguntar si guardás una <strong>llave de acceso</strong> para FORJA: es
                esto, tocá Continuar.
              </p>
              {avisoOferta && (
                <p className="push-nudge__body" role="status" style={{ color: "#fbbf24" }}>
                  {avisoOferta}
                </p>
              )}
              <button type="button" className="push-nudge__cta" disabled={activando} onClick={() => void activar()}>
                {activando ? "Un momento…" : oferta === "otra_vez" ? "Activar de nuevo" : "Activar"}
              </button>
              <button type="button" className="push-nudge__plain" onClick={ahoraNo}>
                Ahora no
              </button>
              <p className="push-nudge__body" style={{ fontSize: "0.75rem", margin: 0 }}>
                Queda sólo en este teléfono. Lo prendés o apagás desde tu Perfil.
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
